import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Archive,
  ArrowLeft,
  CheckCircle2,
  Eye,
  History,
  ListChecks,
  Pencil,
  Play,
  Plus,
  Send,
  Settings,
  Trash2,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { formatDate } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { GatedSection } from '../components/shared/GatedSection';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import {
  AudienceChip,
  canAuthorAtScope,
  canCreateRunAtScope,
  canCreateRunFromTemplate,
  canEditTemplate,
  canEditTemplateInPlace,
  canPublishAtScope,
  canPublishFrom,
  canSubmitForReview,
  canUnpublishAtScope,
  DifficultyChip,
  formatDuration,
  ModeChip,
  OwnerTypeChip,
  previewTagRules,
  QuestionDrawer,
  RunCreateDrawer,
  RunStatusChip,
  ScopeChip,
  StatusChip,
  TemplateDrawer,
  type QuestionDetail,
  type QuestionDrawerMode,
  type RunListItem,
  type TemplateDetail,
  type TemplateDrawerMode,
} from '../components/assessments';

export const Route = createFileRoute('/_authed/academics/assessments/$templateId')({
  component: TemplateDetailPage,
});

type Tab = 'questions' | 'settings' | 'runs' | 'history' | 'activity';

const TAB_OPTIONS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'questions', label: 'Questions' },
  { value: 'settings', label: 'Settings' },
  { value: 'runs', label: 'Runs' },
  { value: 'history', label: 'History' },
  { value: 'activity', label: 'Activity' },
];

function TemplateDetailPage() {
  const { templateId } = Route.useParams();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('questions');
  const [editDrawer, setEditDrawer] = useState<TemplateDrawerMode | null>(null);
  const [runDrawerOpen, setRunDrawerOpen] = useState(false);

  const templateQuery = useQuery({
    queryKey: ['assessments', 'template', templateId] as const,
    queryFn: ({ signal }) =>
      apiRequest<TemplateDetail>(
        `/api/assessments/templates/${encodeURIComponent(templateId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const template = templateQuery.data;

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: ['assessments', 'template', templateId],
    });
    void queryClient.invalidateQueries({ queryKey: ['assessments', 'templates'] });
  };

  return (
    <ListShell
      title={template?.title ?? 'Template'}
      description={
        template
          ? `Version ${template.version} · last updated ${formatDate(template.updatedAt)}`
          : undefined
      }
      primaryAction={
        <Link
          to="/academics/assessments"
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Back to tests & quizzes
        </Link>
      }
    >
      <GatedSection
        error={templateQuery.error}
        title="Template"
        onRetry={() => templateQuery.refetch()}
      >
        {templateQuery.isLoading || !template ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-10 w-72" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <SummaryHeader
              template={template}
              onEdit={() => setEditDrawer({ kind: 'edit', template })}
              onCreateRun={() => setRunDrawerOpen(true)}
              onChanged={invalidate}
            />

            <div
              role="tablist"
              aria-label="Template sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TAB_OPTIONS.map((opt) => {
                const active = tab === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(opt.value)}
                    className={clsx(
                      'rounded-t-md px-4 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {tab === 'questions' ? (
              <QuestionsTab template={template} onChanged={invalidate} />
            ) : null}
            {tab === 'settings' ? <SettingsTab template={template} /> : null}
            {tab === 'runs' ? (
              <RunsTab template={template} onCreateRun={() => setRunDrawerOpen(true)} />
            ) : null}
            {tab === 'history' ? <HistoryTab template={template} /> : null}
            {tab === 'activity' ? (
              <ActivityPanel
                targetType="assessment_template"
                targetId={template.id}
                title="Activity"
                emptyCopy="No template activity recorded yet."
              />
            ) : null}
          </div>
        )}
      </GatedSection>

      <TemplateDrawer
        mode={editDrawer}
        onClose={() => setEditDrawer(null)}
        onSaved={(newId) => {
          setEditDrawer(null);
          invalidate();
          // Versioned edit returns a new id; navigate to it.
          if (template && newId !== template.id) {
            void queryClient.invalidateQueries({
              queryKey: ['assessments', 'template', newId],
            });
          }
        }}
      />

      <RunCreateDrawer
        open={runDrawerOpen}
        onClose={() => setRunDrawerOpen(false)}
        template={template ?? null}
        onCreated={() => {
          setRunDrawerOpen(false);
          void queryClient.invalidateQueries({
            queryKey: ['assessments', 'runs'],
          });
        }}
      />
    </ListShell>
  );
}

// ---------------------------------------------------------------------------
// Summary header
// ---------------------------------------------------------------------------

interface SummaryHeaderProps {
  template: TemplateDetail;
  onEdit: () => void;
  onCreateRun: () => void;
  onChanged: () => void;
}

function SummaryHeader({
  template,
  onEdit,
  onCreateRun,
  onChanged,
}: SummaryHeaderProps) {
  const role = useAuthStore((s) => s.user?.primaryRole);
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const canEdit = canAuthorAtScope(role, template.scope) && canEditTemplate(template.status);
  const canPublish =
    canPublishAtScope(role, template.scope) && canPublishFrom(template.status);
  const canUnpublish =
    canUnpublishAtScope(role, template.scope) && template.status === 'published';
  const canRun =
    canCreateRunAtScope(role, template.scope) && canCreateRunFromTemplate(template.status);
  const canReview = canAuthorAtScope(role, template.scope) && canSubmitForReview(template.status);

  const transitionMutation = useMutation({
    mutationFn: async (action: 'publish' | 'unpublish') =>
      apiRequest<{ id: string; status: string }>(
        `/api/assessments/templates/${encodeURIComponent(template.id)}/${action}`,
        { method: 'POST' },
      ),
    onSuccess: (_res, action) => {
      toast(
        action === 'publish'
          ? 'Template published.'
          : 'Template archived (unpublished).',
        { variant: 'success' },
      );
      onChanged();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not update template.', {
        variant: 'error',
      });
    },
  });

  const reviewMutation = useMutation({
    // Calls the dedicated `draft → in_review` transition. PATCH /templates/:id
    // intentionally does not accept a `status` field (it would conflict with
    // the version-on-edit semantics on published rows), so this is the only
    // route that performs that transition. The endpoint is gated by the same
    // per-scope author matrix the portal uses for the "Submit for review"
    // button, so a UI-visible button always corresponds to a server-accepted
    // request.
    mutationFn: () =>
      apiRequest<{ id: string; status: string }>(
        `/api/assessments/templates/${encodeURIComponent(template.id)}/submit-for-review`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast(
        'Template submitted for review. A nalim or admin will publish it from the templates list.',
        { variant: 'success' },
      );
      onChanged();
    },
    onError: (err: unknown) => {
      toast(
        err instanceof ApiError
          ? err.message
          : 'Could not submit template for review.',
        { variant: 'error' },
      );
    },
  });

  const runPublish = async () => {
    const ok = await confirm({
      title: 'Publish this template?',
      description:
        'Once published, runs can be created against it. Edits will fork a new version.',
      confirmLabel: 'Publish',
    });
    if (!ok) return;
    transitionMutation.mutate('publish');
  };

  const runUnpublish = async () => {
    const ok = await confirm({
      title: 'Unpublish this template?',
      description:
        'This archives the template. Existing runs and attempts stay intact; new runs cannot be created.',
      confirmLabel: 'Unpublish',
      variant: 'danger',
    });
    if (!ok) return;
    transitionMutation.mutate('unpublish');
  };

  return (
    <section className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-xl font-semibold">
              {template.title}
            </h2>
            <ScopeChip scope={template.scope} />
            <ModeChip mode={template.mode} />
            <StatusChip status={template.status} />
            <OwnerTypeChip ownerType={template.ownerType} />
            <span className="rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 font-mono text-[10px] text-[color:var(--color-text-muted)]">
              v{template.version}
            </span>
          </div>
          {template.description ? (
            <p className="mt-2 max-w-3xl text-sm text-[color:var(--color-text)]">
              {template.description}
            </p>
          ) : null}
          <p className="mt-2 font-mono text-[11px] text-[color:var(--color-text-subtle)]">
            template_key: {template.templateKey}
          </p>
          {template.supersedesTemplateId ? (
            <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
              Supersedes earlier version{' '}
              <Link
                to="/academics/assessments/$templateId"
                params={{ templateId: template.supersedesTemplateId }}
                className="text-gold-700 underline"
              >
                view previous
              </Link>
              .
            </p>
          ) : null}
          <CreatorMeta template={template} />
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {canEdit ? (
            <Button
              variant="secondary"
              size="sm"
              icon={<Pencil size={14} aria-hidden="true" />}
              onClick={onEdit}
            >
              Edit metadata
            </Button>
          ) : null}
          {canReview ? (
            <Button
              size="sm"
              variant="secondary"
              icon={<Send size={14} aria-hidden="true" />}
              onClick={() => reviewMutation.mutate()}
              loading={reviewMutation.isPending}
            >
              Submit for review
            </Button>
          ) : null}
          {canPublish ? (
            <Button
              size="sm"
              icon={<CheckCircle2 size={14} aria-hidden="true" />}
              onClick={runPublish}
              loading={transitionMutation.isPending}
            >
              Publish
            </Button>
          ) : null}
          {canRun ? (
            <Button
              size="sm"
              icon={<Play size={14} aria-hidden="true" />}
              onClick={onCreateRun}
            >
              Create run
            </Button>
          ) : null}
          {canUnpublish ? (
            <Button
              size="sm"
              variant="ghost"
              icon={<Archive size={14} aria-hidden="true" />}
              onClick={runUnpublish}
              loading={transitionMutation.isPending}
              className="text-[color:var(--color-text-muted)]"
            >
              Unpublish
            </Button>
          ) : null}
        </div>
      </div>

      <dl className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <Stat label="Questions" value={`${template.activeQuestionCount}`} />
        <Stat
          label="Per attempt"
          value={`${template.questionDrawCount}`}
        />
        <Stat
          label="Duration"
          value={formatDuration(template.durationSeconds)}
        />
        <Stat
          label="Pass mark"
          value={
            template.passThresholdPercent !== null
              ? `${template.passThresholdPercent}%`
              : 'Not set'
          }
        />
        <Stat
          label="Total runs"
          value={
            typeof template.runCount === 'number'
              ? String(template.runCount)
              : '—'
          }
        />
        <Stat
          label="Versions"
          value={
            typeof template.versionCount === 'number'
              ? String(template.versionCount)
              : '—'
          }
        />
      </dl>

      {template.activeQuestionCount < template.questionDrawCount ? (
        <div
          role="alert"
          className="mt-4 rounded-md border border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/5 px-4 py-3 text-sm text-[color:var(--color-danger)]"
        >
          <p className="font-semibold">Pool is shorter than the draw count.</p>
          <p className="mt-0.5 text-[color:var(--color-text)]">
            This template draws {template.questionDrawCount} question
            {template.questionDrawCount === 1 ? '' : 's'} per attempt but only{' '}
            {template.activeQuestionCount} active question
            {template.activeQuestionCount === 1 ? ' is' : 's are'} available.
            Add at least{' '}
            {template.questionDrawCount - template.activeQuestionCount} more
            before publishing or runs will fail at attempt-start time.
          </p>
        </div>
      ) : null}
    </section>
  );
}

interface CreatorMetaProps {
  template: TemplateDetail;
}

/**
 * Inline creator / updater / publisher attribution. The fields are
 * optional on `TemplateDetail` to keep the UI resilient to API rollouts
 * that have not yet shipped the LEFT JOIN; when missing, the row is
 * suppressed so the header doesn't sprout an "Unknown" placeholder.
 */
function CreatorMeta({ template }: CreatorMetaProps) {
  const created = template.createdByName?.trim();
  const updated = template.updatedByName?.trim();
  const published = template.publishedByName?.trim();
  if (!created && !updated && !published) return null;
  const parts: string[] = [];
  if (created) parts.push(`Created by ${created}`);
  if (updated && updated !== created) parts.push(`updated by ${updated}`);
  if (published) parts.push(`published by ${published}`);
  return (
    <p className="mt-2 text-xs text-[color:var(--color-text-muted)]">
      {parts.join(' · ')}
    </p>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2">
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="mt-0.5 font-display text-xl font-bold text-[color:var(--color-text)]">
        {value}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Questions tab
// ---------------------------------------------------------------------------

interface QuestionsTabProps {
  template: TemplateDetail;
  onChanged: () => void;
}

function QuestionsTab({ template, onChanged }: QuestionsTabProps) {
  const role = useAuthStore((s) => s.user?.primaryRole);
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();
  const [drawer, setDrawer] = useState<QuestionDrawerMode | null>(null);

  const editable =
    canAuthorAtScope(role, template.scope) && canEditTemplateInPlace(template.status);

  const questionsQuery = useQuery({
    queryKey: ['assessments', 'questions', template.id] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: QuestionDetail[] }>(
        `/api/assessments/templates/${encodeURIComponent(template.id)}/questions`,
        { signal },
      ),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ ok: true }>(
        `/api/assessments/questions/${encodeURIComponent(id)}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      toast('Question deactivated.', { variant: 'success' });
      void queryClient.invalidateQueries({
        queryKey: ['assessments', 'questions', template.id],
      });
      onChanged();
    },
    onError: (err: unknown) => {
      toast(
        err instanceof ApiError ? err.message : 'Could not delete question.',
        { variant: 'error' },
      );
    },
  });

  const handleDelete = async (q: QuestionDetail) => {
    const ok = await confirm({
      title: 'Deactivate this question?',
      description:
        'The question is hidden from new attempts. Existing attempts that already used it stay intact.',
      confirmLabel: 'Deactivate',
      variant: 'danger',
    });
    if (!ok) return;
    deleteMutation.mutate(q.id);
  };

  const items = questionsQuery.data?.items ?? [];

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-display text-lg font-semibold">Questions</h3>
          <p className="text-xs text-[color:var(--color-text-muted)]">
            {template.activeQuestionCount} active question
            {template.activeQuestionCount === 1 ? '' : 's'} in the pool.
            {template.questionDrawCount > template.activeQuestionCount ? (
              <span className="ml-1 text-[color:var(--color-danger)]">
                Pool is short — add at least{' '}
                {template.questionDrawCount - template.activeQuestionCount} more
                before publishing.
              </span>
            ) : null}
          </p>
        </div>
        {editable ? (
          <Button
            size="sm"
            icon={<Plus size={14} aria-hidden="true" />}
            onClick={() => setDrawer({ kind: 'create', templateId: template.id, scope: template.scope })}
          >
            Add question
          </Button>
        ) : null}
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-6">
          <EmptyState
            icon={<ListChecks size={28} aria-hidden="true" />}
            title={
              template.activeQuestionCount === 0
                ? 'No questions yet'
                : `${template.activeQuestionCount} question${template.activeQuestionCount === 1 ? '' : 's'} authored`
            }
            description={
              editable
                ? 'Use "Add question" to build the pool. Mark at least one correct option per question; add 2–6 options.'
                : 'You do not have permission to edit this template, but you can preview attempts via the runs tab.'
            }
            action={
              editable ? (
                <Button
                  size="sm"
                  icon={<Plus size={14} aria-hidden="true" />}
                  onClick={() =>
                    setDrawer({ kind: 'create', templateId: template.id, scope: template.scope })
                  }
                >
                  Add question
                </Button>
              ) : null
            }
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--color-surface)]">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                  #
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                  Stem
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                  Tag
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                  Difficulty
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                  Points
                </th>
                {editable ? <th className="px-4 py-2" /> : null}
              </tr>
            </thead>
            <tbody>
              {items.map((q, idx) => (
                <tr
                  key={q.id}
                  className="cursor-pointer border-t border-[color:var(--color-border)] transition-colors hover:bg-[color:var(--color-surface)]"
                  onClick={() =>
                    setDrawer(
                      editable
                        ? { kind: 'edit', templateId: template.id, scope: template.scope, question: q }
                        : { kind: 'view', question: q },
                    )
                  }
                >
                  <td className="px-4 py-2 align-middle tabular-nums text-xs text-[color:var(--color-text-muted)]">
                    {q.position ?? idx + 1}
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <p className="line-clamp-2 max-w-[60ch] text-[color:var(--color-text)]">
                      {q.stem}
                    </p>
                  </td>
                  <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                    {q.primaryTag ?? '—'}
                  </td>
                  <td className="px-4 py-2 align-middle">
                    {q.difficulty ? (
                      <DifficultyChip difficulty={q.difficulty} />
                    ) : (
                      <span className="text-xs text-[color:var(--color-text-muted)]">
                        —
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 align-middle text-right tabular-nums">
                    {q.points}
                  </td>
                  {editable ? (
                    <td className="px-4 py-2 align-middle text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDelete(q);
                        }}
                        aria-label={`Deactivate question ${q.id}`}
                        className="rounded-md p-1.5 text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-danger)]/10 hover:text-[color:var(--color-danger)]"
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <QuestionDrawer
        mode={drawer}
        onClose={() => setDrawer(null)}
        onSaved={() => {
          setDrawer(null);
          void queryClient.invalidateQueries({
            queryKey: ['assessments', 'questions', template.id],
          });
          onChanged();
        }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Settings tab
// ---------------------------------------------------------------------------

function SettingsTab({ template }: { template: TemplateDetail }) {
  return (
    <section className="space-y-4">
      <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <header className="mb-3 flex items-center gap-2">
          <Settings size={16} aria-hidden="true" className="text-[color:var(--color-text-muted)]" />
          <h3 className="font-display text-lg font-semibold">Settings</h3>
        </header>

        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <Row label="Mode" value={<ModeChip mode={template.mode} />} />
          <Row
            label="Duration"
            value={
              <span className="text-sm">
                {formatDuration(template.durationSeconds)}
              </span>
            }
          />
          <Row
            label="Questions per attempt"
            value={
              <span className="text-sm tabular-nums">
                {template.questionDrawCount}
              </span>
            }
          />
          <Row
            label="Selection / ordering"
            value={
              <span className="text-sm">
                {template.selectionStrategy} · {template.orderingStrategy}
              </span>
            }
          />
          <Row
            label="Pass threshold"
            value={
              <span className="text-sm">
                {template.passThresholdPercent !== null
                  ? `${template.passThresholdPercent}%`
                  : '—'}
              </span>
            }
          />
          <Row
            label="Max attempts per user"
            value={
              <span className="text-sm tabular-nums">
                {template.maxAttemptsPerUser}
              </span>
            }
          />
          <Row
            label="Show score to taker"
            value={template.showScoreToTaker ? 'Yes' : 'No'}
          />
          <Row
            label="Show answer review"
            value={template.showAnswerReviewToTaker ? 'Yes' : 'No'}
          />
          <Row
            label="Anti-cheat profile"
            value={template.antiCheatProfile ?? '—'}
          />
        </dl>
      </div>

      <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <h4 className="font-display text-base font-semibold">Tag rules</h4>
        <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
          {previewTagRules(template.tagRules)}
        </p>
        {template.tagRules ? (
          <pre className="mt-3 overflow-x-auto rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 font-mono text-xs">
            {JSON.stringify(template.tagRules, null, 2)}
          </pre>
        ) : null}
      </div>

      <p className="text-xs text-[color:var(--color-text-muted)]">
        Settings are edited via the metadata drawer. Click <strong>Edit metadata</strong> at the top to change them.
        Published templates fork into a new version on edit.
      </p>
    </section>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[color:var(--color-border)] pb-2 last:border-b-0 last:pb-0">
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd>{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Runs tab
// ---------------------------------------------------------------------------

interface RunsTabProps {
  template: TemplateDetail;
  onCreateRun: () => void;
}

function RunsTab({ template, onCreateRun }: RunsTabProps) {
  const role = useAuthStore((s) => s.user?.primaryRole);
  const navigate = useNavigate();
  const canRun =
    canCreateRunAtScope(role, template.scope) &&
    canCreateRunFromTemplate(template.status);

  const runsQuery = useQuery({
    queryKey: ['assessments', 'template-runs', template.id] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: RunListItem[]; total: number }>(
        '/api/assessments/runs',
        {
          signal,
          query: { templateId: template.id, page: 1, pageSize: 100 },
        },
      ),
  });

  const items = runsQuery.data?.items ?? [];

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-display text-lg font-semibold">Runs</h3>
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Each run draws a fresh question set per attempt from this
            template's active pool.
          </p>
        </div>
        {canRun ? (
          <Button
            size="sm"
            icon={<Play size={14} aria-hidden="true" />}
            onClick={onCreateRun}
          >
            Create run
          </Button>
        ) : null}
      </div>

      {runsQuery.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : items.length === 0 ? (
        <EmptyState
          title="No runs yet"
          description={
            canRun
              ? 'Once the template is ready, create a run to surface it to candidates or students.'
              : 'No runs have been created against this template yet.'
          }
          action={
            canRun ? (
              <Button
                size="sm"
                icon={<Play size={14} aria-hidden="true" />}
                onClick={onCreateRun}
              >
                Create run
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--color-surface)]">
              <tr>
                <Th>Title</Th>
                <Th>Audience</Th>
                <Th>Period</Th>
                <Th>Status</Th>
                <Th>Opens</Th>
                <Th>Closes</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((run) => (
                <tr
                  key={run.id}
                  onClick={() =>
                    void navigate({
                      to: '/academics/assessments/runs/$runId',
                      params: { runId: run.id },
                    })
                  }
                  className="cursor-pointer border-t border-[color:var(--color-border)] transition-colors hover:bg-[color:var(--color-surface)]"
                >
                  <td className="px-4 py-2 align-middle font-medium">
                    {run.title}
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <AudienceChip type={run.audienceType} />
                  </td>
                  <td className="px-4 py-2 align-middle font-mono text-xs text-[color:var(--color-text-muted)]">
                    {run.periodKey ?? '—'}
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <RunStatusChip status={run.status} />
                  </td>
                  <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                    {run.opensAt ? formatDate(run.opensAt, { withTime: true }) : '—'}
                  </td>
                  <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                    {run.closesAt ? formatDate(run.closesAt, { withTime: true }) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
      {children}
    </th>
  );
}

// ---------------------------------------------------------------------------
// History tab
// ---------------------------------------------------------------------------

function HistoryTab({ template }: { template: TemplateDetail }) {
  const navigate = useNavigate();
  const historyQuery = useQuery({
    queryKey: ['assessments', 'template-history', template.templateKey] as const,
    queryFn: ({ signal }) =>
      apiRequest<{
        items: Array<{
          id: string;
          version: number;
          status: string;
          publishedAt: string | null;
          updatedAt: string;
          supersedesTemplateId: string | null;
        }>;
        total: number;
      }>('/api/assessments/templates', {
        signal,
        query: {
          templateKey: template.templateKey,
          page: 1,
          pageSize: 50,
        },
      }),
  });

  const items = useMemo(
    () =>
      (historyQuery.data?.items ?? [])
        .slice()
        .sort((a, b) => b.version - a.version),
    [historyQuery.data],
  );

  return (
    <section className="space-y-3">
      <header className="flex items-center gap-2">
        <History size={16} aria-hidden="true" className="text-[color:var(--color-text-muted)]" />
        <h3 className="font-display text-lg font-semibold">Version history</h3>
      </header>
      {historyQuery.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : items.length === 0 ? (
        <p className="text-sm text-[color:var(--color-text-muted)]">
          Only one version on file.
        </p>
      ) : (
        <ol className="space-y-2">
          {items.map((v) => (
            <li
              key={v.id}
              className={clsx(
                'flex items-center justify-between rounded-md border px-4 py-3',
                v.id === template.id
                  ? 'border-gold-500 bg-gold-50 dark:bg-gold-700/15'
                  : 'border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]',
              )}
            >
              <div>
                <p className="text-sm font-medium">
                  Version {v.version}{' '}
                  <span className="ml-2 text-xs uppercase tracking-wider text-[color:var(--color-text-muted)]">
                    {v.status}
                  </span>
                </p>
                <p className="text-xs text-[color:var(--color-text-muted)]">
                  Updated {formatDate(v.updatedAt)}
                  {v.publishedAt ? ` · published ${formatDate(v.publishedAt)}` : ''}
                </p>
              </div>
              {v.id === template.id ? (
                <span className="text-xs font-semibold text-gold-700">
                  This version
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    void navigate({
                      to: '/academics/assessments/$templateId',
                      params: { templateId: v.id },
                    })
                  }
                  className="inline-flex items-center gap-1 text-xs text-gold-700 hover:underline"
                >
                  <Eye size={12} aria-hidden="true" /> View
                </button>
              )}
            </li>
          ))}
        </ol>
      )}
      <p className="mt-3 inline-flex items-center gap-1 text-xs text-[color:var(--color-text-muted)]">
        <XCircle size={12} aria-hidden="true" /> Older versions stay read-only; new attempts always use the active version.
      </p>
    </section>
  );
}
